import GenericItemsView from "views/ItemsView/GenericItemsView";
import { type CustomerView } from "../types/models";
import { type ColumnOption } from "interfaces/columnOptions";
import { SortingDirection } from "enums";
import { NoResults, NoResultsWithButton, NoSearchResults, SearchInput } from "components";
import * as rtnEvents from "../../../Application/services/realTimeNotification/events/people/peopleEvents";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Columns, columnToParamMap } from "./Customers/getColumnOptions";

type Props = {
  selectedIds?: number[];
  setSelectedIds: (ids: number[]) => void;
  hasChildCustomers?: boolean;
  renderAddCustomersButton?: () => ReactNode;
  items: CustomerView[];
  moboAccountId?: number;
  isLoading: boolean;
  itemsCount: number;
  getData: (
    skip: number,
    top: number,
    sortingColumnName: string,
    sortingDirection: SortingDirection,
    templateId?: number,
    searchTerm?: string,
  ) => void;
  templateId?: number;
  columnOptions: ColumnOption<CustomerView>[];
  columnMap?: { [key: string]: string };
  disabled?: boolean;
};

export const GroupTemplateCustomers = (props: Props) => {
  const {
    selectedIds,
    setSelectedIds,
    items,
    hasChildCustomers,
    renderAddCustomersButton,
    isLoading,
    itemsCount,
    getData,
    templateId,
    columnOptions,
    disabled,
    columnMap,
  } = props;

  const reloadListItemsRef = useRef<(enableSorting: boolean) => void>();
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    reloadListItemsRef.current && reloadListItemsRef.current(true);
  }, [searchTerm]);

  const onSearchChanged = (term: string) => {
    setSearchTerm(term);
  };

  const loadCustomers = (
    skip: number = 0,
    top: number = 10,
    sortingColumnName: string = "",
    sortingDirection: SortingDirection = SortingDirection.Descending,
  ) => {
    const sortColumn = (columnMap ?? columnToParamMap)[sortingColumnName];
    getData(skip, top, sortColumn, sortingDirection, templateId, searchTerm);
  };

  const noResultsContent = useMemo(() => {
    if (searchTerm) {
      return <NoSearchResults />;
    }
    if (!renderAddCustomersButton) {
      return <NoResults title="You don't have any available customers." iconClassName="fa-users" />;
    }
    let title, description, actionText;
    if (hasChildCustomers) {
      title = "You haven't added any customers";
      description = "Looks like you haven't added any customers to this template yet.";
      actionText = "Add some now using the button below.";
    } else {
      title = "You don't have any available customers";
      description = "Looks like you don't have any customers yet.";
      actionText = "Create one now using the button below.";
    }
    return (
      <NoResultsWithButton
        title={title}
        iconClassName="fa-users"
        description={description}
        actionText={actionText}
        actionButton={renderAddCustomersButton?.()}
      />
    );
  }, [hasChildCustomers, renderAddCustomersButton, searchTerm]);

  return (
    <GenericItemsView
      fetchData={loadCustomers}
      items={items}
      isLoading={isLoading}
      dataCount={itemsCount}
      columnOptions={columnOptions}
      isSelectDisabled={(item) => item.isAdded || !!disabled}
      onSelectedItemChanged={setSelectedIds}
      noResultsContent={noResultsContent}
      renderSearch={() => <SearchInput placeholder="Search for customers..." onChange={onSearchChanged} />}
      setReloadListItems={(cb) => (reloadListItemsRef.current = cb)}
      selectedIds={selectedIds}
      listViewRtnEvents={[
        rtnEvents.AccountAddedToTemplateSuccess,
        rtnEvents.AccountRemovedFromTemplateSuccess,
        rtnEvents.UpdateAllTemplateGroupAccountPackMessageSuccess,
      ]}
      defaultSortingColumnName={!searchTerm ? Columns.Added : ""}
    />
  );
};
